
import { useEffect, useRef, useState, Children, isValidElement, cloneElement } from "react";
import { createCustomEqual } from "fast-equals";

export default function GoogleMap ({ onClick, onIdle, children, style, ...options }) {
    const ref = useRef(null); // <HTMLDivElement>
    const [map, setMap] = useState(); // <google.maps.Map>

    useEffect(() => {
        if (ref.current && !map) {
            setMap(new window.google.maps.Map(ref.current, {}));
        }
    }, [ref, map]);

    // because React does not do deep comparisons, a custom hook is used
    // see discussion in https://github.com/googlemaps/js-samples/issues/946
    useDeepCompareEffectForMaps(() => {
        if (map) {
            map.setOptions(options);
        }
    }, [map, options]);

    useEffect(() => {
        if (map) {
            ["click", "idle"].forEach((eventName) =>
                window.google.maps.event.clearListeners(map, eventName)
            );

            if (onClick) {
                map.addListener("click", onClick);
            }

            if (onIdle) {
                map.addListener("idle", () => onIdle(map));
            }
        }
    }, [map, onClick, onIdle]);

    return (
        <>
            <div ref={ref} style={style} />
            {
                Children.map(children, (child) => {
                    if (isValidElement(child)) {
                        // set the map prop on the child component
                        return cloneElement(child, { map });
                    }
                    return <></>
                })
            }
        </>
    );
};

//
// Google Maps Utilities
//
const deepCompareEqualsForMaps = createCustomEqual(
    (deepEqual) => (a, b) => {
    if (
        // isLatLngLiteral(a) ||
        a instanceof window.google.maps.LatLng ||
        // isLatLngLiteral(b) ||
        b instanceof window.google.maps.LatLng
    ) {
        return new window.google.maps.LatLng(a).equals(new window.google.maps.LatLng(b));
    }

    // TODO extend to other types

    // use fast-equals for other objects
    return deepEqual(a, b);
    }
);

function useDeepCompareMemoize(value) {
    const ref = useRef();

    if (!deepCompareEqualsForMaps(value, ref.current)) {
        ref.current = value;
    }

    return ref.current;
}

function useDeepCompareEffectForMaps(callback, dependencies) {
    // eslint-disable-next-line
    useEffect(callback, dependencies.map(useDeepCompareMemoize));
}